import React, { useState } from 'react';
import {
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@strivelabs/ui-core';
import { IconButton } from '@strivelabs/ui-core/v2';
import { Maximize2 } from 'lucide-react';

export interface ChartWrapperProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** Chart component and its props for re-rendering in expanded view */
  chartComponent?: React.ComponentType<any>;
  chartProps?: Record<string, any>;
}

export const ChartWrapper: React.FC<ChartWrapperProps> = ({
  title,
  description,
  children,
  className,
  headerClassName,
  contentClassName,
  expandable = true,
  expandedHeight = 500,
  chartComponent: ChartComponent,
  chartProps,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Create expanded chart by re-rendering from scratch with isExpanded=true
  const renderExpandedChart = () => {
    if (ChartComponent && chartProps) {
      // Re-render the chart component with isExpanded flag and expanded height
      return (
        <ChartComponent
          {...chartProps}
          isExpanded={true}
          height={expandedHeight}
          expandable={false}
        />
      );
    }
    // Fallback: show normal children if chartComponent not provided
    return children;
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-4 bg-custom-bg-primary border border-custom-border rounded-lg p-4 relative',
          className
        )}
      >
        {/* Expand Button - Top Right */}
        {expandable && (
          <IconButton
            variant="secondary"
            size="2xs"
            onClick={() => setIsExpanded(true)}
            className="absolute top-3 right-3 z-10"
            aria-label="Expand chart"
          >
            <Maximize2 className="size-3.5 text-muted-foreground" />
          </IconButton>
        )}

        {/* Header */}
        {(title || description) && (
          <div className={cn('flex flex-col gap-1', headerClassName)}>
            {title && (
              <h3 className="text-sm text-custom-text-secondary font-semibold leading-tight tracking-tight pr-8">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-sm text-muted-foreground pr-8">{description}</p>
            )}
          </div>
        )}

        {/* Chart Content */}
        <div className={cn('w-full', contentClassName)}>{children}</div>
      </div>

      {/* Expanded Dialog */}
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] overflow-auto">
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <div className="mt-4">{renderExpandedChart()}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};
